import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Shield, Search, AlertTriangle, Link2, EyeOff, FileText, ArrowRight,
  Upload, BarChart3, Download, Check, Lock, Database, Eye, ServerCog, Zap, Sun, Moon, ChevronDown
} from 'lucide-react';

const features = [
  { key: 'hardcoded', icon: Search },
  { key: 'formula', icon: AlertTriangle },
  { key: 'circular', icon: BarChart3 },
  { key: 'external', icon: Link2 },
  { key: 'hidden', icon: EyeOff },
  { key: 'ai', icon: FileText },
];

const steps = [
  { key: 'step1', icon: Upload, num: '01' },
  { key: 'step2', icon: BarChart3, num: '02' },
  { key: 'step3', icon: Download, num: '03' },
];

export default function Landing() {
  const { t, language, changeLanguage } = useTranslation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // 'auditor' | 'firm' | null
  const [isAnnual, setIsAnnual] = useState(true); // Annual by default (2 months free)
  const location = useLocation();

  // Scroll to #pricing when redirected from Dashboard upgrade button
  useEffect(() => {
    if (location.hash === '#pricing') {
      const timer = setTimeout(() => {
        const el = document.getElementById('pricing');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const handlePlanClick = async (planId) => {
    const interval = isAnnual ? 'year' : 'month';
    if (user) {
      setCheckoutLoading(planId);
      try {
        const currency = language === 'fr' ? 'eur' : 'usd';
        const res = await billingAPI.checkout(window.location.origin, planId, currency, language, interval);
        if (res.data?.url) window.location.href = res.data.url;
      } catch { /* ignore */ }
      finally { setCheckoutLoading(null); }
    } else {
      localStorage.setItem('intended_plan', JSON.stringify({ plan: planId, interval }));
      navigate('/login?mode=register');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] text-slate-900 dark:text-white transition-colors" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-[#050505]/90 backdrop-blur-lg border-b border-slate-200 dark:border-white/5">
        <div className="max-w-[90rem] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg tracking-tight">SheetArmor</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-sm font-body transition-colors">{t('nav.product')}</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-sm font-body transition-colors">{t('nav.pricing')}</a>
            <button
              onClick={toggleTheme}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-sm font-body border border-slate-200 dark:border-slate-200 dark:border-white/10 px-2.5 py-1 rounded-sm transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            <button
              onClick={() => changeLanguage(language === 'en' ? 'fr' : 'en')}
              className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-sm font-body flex items-center gap-1.5 border border-slate-200 dark:border-white/10 px-2.5 py-1 rounded-sm transition-colors"
              data-testid="lang-toggle"
            >
              {language === 'en' ? '\ud83c\uddfa\ud83c\uddf8' : '\ud83c\uddeb\ud83c\uddf7'} {language.toUpperCase()}
            </button>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:border-white/20 dark:text-white dark:hover:bg-white/5 text-xs font-body tracking-widest h-9 rounded-sm" data-testid="nav-dashboard-button">
                  DASHBOARD
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white text-xs font-body tracking-wide h-9" data-testid="nav-login-button">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/login?mode=register">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest h-9 rounded-sm" data-testid="nav-start-button">
                    {t('nav.start')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden pt-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(37,99,235,0.08)_0%,transparent_60%)]" />
        <div className="max-w-[90rem] mx-auto px-6 py-24 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-blue-600/30 bg-blue-600/5 px-3 py-1.5 rounded-sm mb-8 animate-fade-in">
              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
              <span className="text-blue-400 text-xs font-body tracking-widest uppercase">{t('hero.tag')}</span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tight leading-none mb-6 animate-fade-in-up">
              {t('hero.title')}
            </h1>
            <p className="text-slate-600 dark:text-neutral-400 text-lg md:text-xl font-body font-light leading-relaxed max-w-2xl mb-10 animate-fade-in-up delay-100">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 mb-16 animate-fade-in-up delay-200">
              <Link to="/login?mode=register">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-xs font-body tracking-widest rounded-sm" data-testid="hero-cta-button">
                  {t('hero.cta')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
              <a href="#how">
                <Button variant="outline" className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:border-white/20 dark:text-white dark:hover:bg-white/5 h-12 px-8 text-xs font-body tracking-widest rounded-sm" data-testid="hero-cta2-button">
                  {t('hero.cta2')}
                </Button>
              </a>
            </div>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-sm overflow-hidden animate-fade-in-up delay-300">
            {['stat1', 'stat2', 'stat3'].map((key) => (
              <div key={key} className="bg-slate-50 dark:bg-[#0A0A0A] p-6">
                <p className="font-heading text-3xl md:text-4xl text-slate-900 dark:text-white mb-2">{t(`hero.${key}.value`)}</p>
                <p className="text-slate-600 dark:text-neutral-500 text-sm font-body">{t(`hero.${key}.label`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="mb-16">
            <span className="text-blue-600 text-xs font-body tracking-widest uppercase">{t('features.tag')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 mb-4">{t('features.title')}</h2>
            <p className="text-slate-600 dark:text-neutral-400 text-lg md:text-lg font-body font-light max-w-2xl">{t('features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 dark:bg-white/10 rounded-sm overflow-hidden">
            {features.map(({ key, icon: Icon }) => (
              <div key={key} className="bg-slate-50 dark:bg-[#0A0A0A] p-8 hover:bg-slate-100 dark:hover:bg-[#111] transition-colors group" data-testid={`feature-${key}`}>
                <Icon className="w-5 h-5 text-blue-600 mb-4 group-hover:translate-y-[-2px] transition-transform" strokeWidth={1.5} />
                <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t(`feat.${key}.title`)}</h3>
                <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed">{t(`feat.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-[90rem] mx-auto px-6">
          <span className="text-blue-600 text-xs font-body tracking-widest uppercase">{t('how.tag')}</span>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 mb-16">{t('how.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map(({ key, icon: Icon, num }) => (
              <div key={key} className="relative" data-testid={`how-${key}`}>
                <span className="font-mono text-6xl text-slate-200 dark:text-white/5 absolute -top-4 -left-2">{num}</span>
                <div className="relative z-10 pt-8">
                  <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4">
                    <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl text-slate-900 dark:text-white mb-2">{t(`how.${key}.title`)}</h3>
                  <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed">{t(`how.${key}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security - Bank-Grade Protection */}
      <section id="security" className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5 bg-gradient-to-b from-slate-50 to-white dark:from-[#050505] dark:to-[#0A0A0A]">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 text-xs font-body tracking-widest uppercase">{t('security.tag')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 mb-4">
              {t('security.title')}
            </h2>
            <p className="text-slate-600 dark:text-neutral-400 text-lg md:text-lg font-body font-light max-w-2xl mx-auto">
              {t('security.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Zero-Retention */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <Database className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.zero_retention.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.zero_retention.desc').replace(/détruits physiquement après 24h|physically destroyed after 24h/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>

            {/* Zero-Training */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.zero_training.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.zero_training.desc').replace(/s'engagent contractuellement|contractually commit/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>

            {/* Hébergement Souverain */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <ServerCog className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.sovereignty.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.sovereignty.desc').replace(/100% européenne|100% European/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>

            {/* Read-Only Analysis */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <Eye className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.read_only.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.read_only.desc').replace(/garantie|guaranteed/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>

            {/* Bank-Grade Encryption */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <Lock className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.encryption.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.encryption.desc').replace(/Sécurité bancaire|Banking security/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>

            {/* Fast & Efficient */}
            <div className="bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm p-6 hover:border-blue-600/30 transition-colors group">
              <div className="w-10 h-10 border border-blue-600/30 bg-blue-600/5 flex items-center justify-center rounded-sm mb-4 group-hover:bg-blue-600/10 transition-colors">
                <Zap className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 dark:text-white mb-2">{t('security.fast.title')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body leading-relaxed" dangerouslySetInnerHTML={{
                __html: t('security.fast.desc').replace(/moins de 5 minutes|less than 5 minutes/g, '<strong class="text-slate-900 dark:text-white">$&</strong>')
              }} />
            </div>
          </div>

          {/* Security Badge */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-2 bg-blue-600/10 border border-blue-600/30 rounded-sm px-6 py-3">
              <Shield className="w-4 h-4 text-blue-600" strokeWidth={2} />
              <p className="text-slate-700 dark:text-neutral-300 text-xs font-body">
                {t('security.badge')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-600 text-xs font-body tracking-widest uppercase">{t('pricing.tag')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3">{t('pricing.title')}</h2>
            <p className="text-slate-500 dark:text-neutral-600 text-xs font-body mt-3 uppercase tracking-wider">{t('pricing.corporate')}</p>
            {/* Pill Toggle: Monthly | Annual with Save 17% badge */}
            <div className="flex items-center justify-center gap-3 mt-8">
              <div className="relative inline-flex items-center p-1 bg-slate-200 dark:bg-white/10 rounded-full">
                <button
                  type="button"
                  onClick={() => setIsAnnual(false)}
                  className={`relative z-10 px-5 py-2 text-sm font-body transition-colors rounded-full ${!isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-neutral-500 hover:text-slate-700 dark:hover:text-neutral-400'}`}
                >
                  {t('pricing.monthly')}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAnnual(true)}
                  className={`relative z-10 px-5 py-2 text-sm font-body transition-colors rounded-full ${isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-neutral-500 hover:text-slate-700 dark:hover:text-neutral-400'}`}
                >
                  {t('pricing.annual')}
                </button>
                <div
                  className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white dark:bg-slate-800 rounded-full shadow-sm transition-all duration-200 ease-out ${isAnnual ? 'left-1 translate-x-full' : 'left-1 translate-x-0'}`}
                  aria-hidden
                />
              </div>
              <span className="bg-green-600 text-white text-xs font-body font-medium tracking-wide px-3 py-1.5 rounded-full whitespace-nowrap">
                {t('pricing.save_17')}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto items-stretch">
            {/* Auditor */}
            <div className="border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0A0A0A] rounded-sm p-8 flex flex-col h-full" data-testid="pricing-auditor">
              <h3 className="font-heading text-2xl text-slate-900 dark:text-white mb-1">{t('pricing.plan1')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body mb-6">{t('pricing.plan1.desc')}</p>
              <div className="flex flex-col gap-0.5 mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-5xl text-slate-900 dark:text-white">
                    {isAnnual
                      ? (language === 'fr' ? '82€' : '$82')
                      : (language === 'fr' ? '99€' : '$99')}
                  </span>
                  <span className="text-neutral-500 font-body text-sm">{t('pricing.plan1.period')}</span>
                </div>
                {isAnnual && (
                  <p className="text-xs text-slate-500 dark:text-neutral-500 font-body">
                    {t('pricing.billed_yearly').replace('{price}', language === 'fr' ? '990€' : '$990')}
                  </p>
                )}
              </div>
              <div className="space-y-3 mb-8 flex-1">
                {['feat1', 'feat2', 'feat3', 'feat4', 'feat5'].map((key) => (
                  <div key={key} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2} />
                    <span className="text-slate-700 dark:text-neutral-300 text-sm font-body">{t(`pricing.plan1.${key}`)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-auto">
                <Button
                  variant="outline"
                  onClick={() => handlePlanClick('auditor')}
                  disabled={!!checkoutLoading}
                  className="w-full border-slate-300 text-slate-900 hover:bg-slate-100 dark:border-white/20 dark:text-white dark:hover:bg-white/5 h-12 text-xs font-body tracking-widest rounded-sm disabled:opacity-50"
                  data-testid="pricing-auditor-cta"
                >
                  {checkoutLoading === 'auditor' ? '...' : t('pricing.plan1.cta')}
                </Button>
                <p className="text-center text-xs text-slate-500 dark:text-neutral-500 mt-2">{t('pricing.no_commitment')}</p>
              </div>
            </div>
            {/* Firm */}
            <div className="border border-blue-600/40 bg-slate-50 dark:bg-[#0A0A0A] rounded-sm p-8 relative flex flex-col h-full" data-testid="pricing-firm">
              <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-body tracking-widest px-3 py-1 rounded-bl-sm">RECOMMENDED</div>
              <h3 className="font-heading text-2xl text-slate-900 dark:text-white mb-1">{t('pricing.plan2')}</h3>
              <p className="text-slate-600 dark:text-neutral-500 text-sm font-body mb-6">{t('pricing.plan2.desc')}</p>
              <div className="flex flex-col gap-0.5 mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-5xl text-slate-900 dark:text-white">
                    {isAnnual
                      ? (language === 'fr' ? '207€' : '$207')
                      : (language === 'fr' ? '249€' : '$249')}
                  </span>
                  <span className="text-neutral-500 font-body text-sm">{t('pricing.plan2.period')}</span>
                </div>
                {isAnnual && (
                  <p className="text-xs text-slate-500 dark:text-neutral-500 font-body">
                    {t('pricing.billed_yearly').replace('{price}', language === 'fr' ? '2490€' : '$2490')}
                  </p>
                )}
              </div>
              <div className="space-y-3 mb-8 flex-1">
                {['feat1', 'feat2', 'feat3', 'feat4', 'feat5', 'feat6'].map((key) => (
                  <div key={key} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2} />
                    <span className="text-slate-700 dark:text-neutral-300 text-sm font-body">{t(`pricing.plan2.${key}`)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-auto">
                <Button
                  onClick={() => handlePlanClick('firm')}
                  disabled={!!checkoutLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-xs font-body tracking-widest rounded-sm disabled:opacity-50"
                  data-testid="pricing-firm-cta"
                >
                  {checkoutLoading === 'firm' ? '...' : t('pricing.plan2.cta')}
                </Button>
                <p className="text-center text-xs text-slate-500 dark:text-neutral-500 mt-2">{t('pricing.no_commitment')}</p>
              </div>
            </div>
          </div>
          <p className="mt-8 text-center text-slate-500 dark:text-neutral-500 text-xs font-body max-w-2xl mx-auto">
            {t('pricing.legal_notice')}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(37,99,235,0.06)_0%,transparent_60%)]" />
        <div className="max-w-[90rem] mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-4">{t('cta.title')}</h2>
          <p className="text-slate-600 dark:text-neutral-400 text-lg md:text-lg font-body font-light max-w-xl mx-auto mb-10">{t('cta.subtitle')}</p>
          <Link to="/login?mode=register">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200 h-12 px-10 text-xs font-body tracking-widest rounded-sm" data-testid="final-cta-button">
              {t('cta.button')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-blue-600 text-xs font-body tracking-widest uppercase">{t('faq.tag')}</span>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3">{t('faq.title')}</h2>
            </div>

            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={index}
                    className="border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0A0A0A] rounded-sm overflow-hidden hover:border-blue-600/30 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left group"
                      data-testid={`faq-question-${index}`}
                    >
                      <h3 className="font-heading text-lg text-slate-900 dark:text-white pr-4">
                        {t(`faq.q${index}`)}
                      </h3>
                      <ChevronDown
                        className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        strokeWidth={1.5}
                      />
                    </button>
                    {isOpen && (
                      <div
                        className="px-6 pb-5 animate-fade-in"
                        data-testid={`faq-answer-${index}`}
                      >
                        <p className="text-slate-600 dark:text-neutral-400 text-sm font-body leading-relaxed">
                          {t(`faq.a${index}`)}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-white/5 py-12">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
              <span className="text-slate-600 dark:text-neutral-500 text-sm font-body">{t('footer.tagline')}</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 dark:text-neutral-500 dark:hover:text-white text-xs font-body transition-colors">
                {t('footer.contact')}
              </Link>
              <Link to="/legal" className="text-slate-600 hover:text-slate-900 dark:text-neutral-500 dark:hover:text-white text-xs font-body transition-colors">
                {t('footer.legal')}
              </Link>
              <Link to="/terms" className="text-slate-600 hover:text-slate-900 dark:text-neutral-500 dark:hover:text-white text-xs font-body transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/privacy" className="text-slate-600 hover:text-slate-900 dark:text-neutral-500 dark:hover:text-white text-xs font-body transition-colors">
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
          <p className="text-slate-500 dark:text-neutral-700 text-xs font-body text-center">&copy; 2026 {t('footer.copy')}</p>
        </div>
      </footer>
    </div>
  );
}
